import validationEngine from 'devextreme/ui/validation_engine'
import { omitBy, isObject, indexOf } from 'lodash'
import { Trans } from 'react-i18next'
import { renderToString } from 'react-dom/server'
import { formatNumber } from 'devextreme/localization'
import { NumberFormat } from '@/constants'
import CheckBox from 'devextreme/ui/check_box'
import { parseValueToBooleanNumber } from '@/utils'

export const setDataFieldErrorTabActive = (errors, tabs, setSelected) => {
  if (Object.keys(errors).length) {
    const dataField = (Object.keys(errors)[0]).split('.')[0]

    for (let i = 0; i < tabs.length; i++) {
      const hasError = getDataFieldErrorTab(dataField, tabs[i].items)

      if (hasError) {
        setSelected(i)
        break
      }
    }
  }
}

const getDataFieldErrorTab = (dataField, items) => {
  let hasError = false

  for (let i = 0; i < items.length; i++) {
    if (items[i].items) {
      return getDataFieldErrorTab(dataField, items[i].items)
    }

    if (items[i].dataField.split('.')[0] === dataField) {
      hasError = true
      break
    }
  }

  return hasError
}

export const validateForm = (validationGroup, setError, clearErrors) => {
  const validationResult = validationEngine.validateGroup(validationGroup)
  clearErrors()

  if (!validationResult.isValid) {
    const brokenRules = validationResult.brokenRules

    brokenRules.forEach(rule => {
      setError(
        rule.validator.option('adapter').editor.option('name').split('.')[0],
        rule.message
      )
    })
  }

  return validationResult.isValid
}

export const setFormDataFieldErrorTabActive = (errors, tabsInputs, setSelected) => {
  if (Object.keys(errors).length) {
    const dataField = (Object.keys(errors)[0]).split('.')[0]

    for (let i = 0; i < tabsInputs.length; i++) {
      for (let y = 0; y < tabsInputs[i].length; y++) {
        if (tabsInputs[i][y].name.split('.')[0] === dataField) {
          setSelected(i)
          break
        }
      }
    }
  }
}

export function calculateFilterExpressionTransField (filterValue, selectedFilterOperation, target) {
  const column = this
  const dataFieldSplit = column.dataField.split('.')
  const dbTable = column.dbTable

  return [`${dbTable ? dbTable + '.' : ''}${dataFieldSplit[0]}->>"$.${dataFieldSplit[1]}"`, selectedFilterOperation ?? 'contains', filterValue]
}

export function calculateFilterExpressionField (filterValue, selectedFilterOperation, target, columnRef = null) {
  const column = this ?? columnRef
  const dbTable = column.dbTable ?? column.searchTable
  const relatedSearchTable = column.relatedSearchTable
  const relatedSearchColumn = column.relatedSearchDisplayColumn ?? column.relatedSearchColumn
  const relatedFilterColumn = column.relatedFilterColumn ?? column.dataField
  const localFilter = column.localFilter

  selectedFilterOperation = column.dataType === 'boolean' ? '=' : selectedFilterOperation
  filterValue = column.dataType === 'boolean' ? parseValueToBooleanNumber(filterValue) : filterValue

  const initialDataFieldFilter = [column.dataField, 'contains', '']

  const skipFilter = () => {
    if (!localFilter && column?.lookup?.dataSource && Array.isArray(column.lookup.dataSource)) {
      const option = column.lookup.dataSource.find(f => f.value === filterValue)
      const searchPanelText = column.dataGridEditor?.component?.option('searchPanel.text')

      if (!option.label.toLowerCase().includes(searchPanelText?.toLowerCase())) {
        return true
      }
    }

    return false
  }

  if (target === 'search') {
    if (relatedSearchTable && relatedSearchColumn) {
      if (Array.isArray(relatedSearchColumn)) {
        const conditions = relatedSearchColumn.map(relatedSearchColumn => [
          `${relatedSearchTable && !localFilter ? relatedSearchTable + '.' : ''}${relatedSearchColumn}`,
          selectedFilterOperation ?? 'contains',
          filterValue
        ])

        if (!localFilter) {
          conditions.push(initialDataFieldFilter)
        }

        return conditions.reduce((acc, condition, index) => {
          if (index > 0) {
            acc.push('or')
          }
          acc.push(condition)
          return acc
        }, [])
      }

      if (skipFilter()) {
        return
      }

      return [
        [`${relatedSearchTable && !localFilter ? relatedSearchTable + '.' : ''}${relatedSearchColumn}`, selectedFilterOperation ?? 'contains', filterValue],
        ...(!localFilter ? ['or', initialDataFieldFilter] : [])
      ]
    }

    if (skipFilter()) {
      return
    }
  }

  return [
    [`${dbTable && !localFilter ? dbTable + '.' : ''}${relatedFilterColumn}`, selectedFilterOperation ?? 'contains', filterValue],
    ...(!localFilter ? ['or', initialDataFieldFilter] : [])
  ]
}

export const getAllParentNodes = (treeListInstance, parentKeys, skipParent) => {
  let result = []

  parentKeys.forEach(function (key) {
    const insertIndex = result.length
    const node = treeListInstance.getNodeByKey(key)
    let parentNode = node.parent
    const childKeys = node.children.map(function (child) {
      return child.key
    })

    // eslint-disable-next-line no-unmodified-loop-condition
    while (parentNode.level >= 0 && !skipParent) {
      if (result.filter(function (nodeItem) { return nodeItem.key === parentNode.key }).length === 0 && treeListInstance.isRowSelected(parentNode.key)) {
        result.splice(insertIndex, 0, parentNode)
        parentNode = parentNode.parent
      } else {
        break
      }
    }

    result.push(node)
    result = result.concat(getAllParentNodes(treeListInstance, childKeys, true))
  })

  return result
}

export const getAllParentNodesParsed = (treeListInstance, parentKeys, skipParent) => {
  return getAllParentNodes(treeListInstance, parentKeys, skipParent).map(m => m.data)
}

export const cleanDataRelationships = (data, returnFormData = false, externalIgnore = []) => {
  const ignore = [
    'name',
    'product_name',
    'description',
    'featured_image',
    'image',
    'logo',
    'role_ids',
    'days',
    'cert_path',
    'domain_zone',
    'slogan',
    'company_greeting',
    'content',
    'model_type',
    'bcc_recipients',
    'partner_logo',
    'stamp_image',
    'signature_image',
    'subject',
    ...externalIgnore
  ]

  const newData = omitBy(data, (value, key) => {
    return isObject(value) && indexOf(ignore, key) < 0
  })

  if (!returnFormData) {
    return newData
  }

  const formData = new FormData()

  buildFormData(formData, newData)

  return formData
}

export const buildFormData = (formData, data, parentKey) => {
  if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File) && !(data instanceof Blob)) {
    Object.keys(data).forEach(key => {
      buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key)
    })
  } else {
    const value = data == null ? '' : data

    formData.append(parentKey, value)
  }
}

export const wrapTextSearchSelectBox = (e) => {
  if (e.editorName === 'dxSelectBox') {
    e.editorOptions.onPopupInitialized = (arg) => {
      arg.popup.content().classList.add('select-box-wrap-text')
    }
  }
}

export const getLoadOptions = (dataGridRef) => {
  const grid = dataGridRef?.current?.instance

  if (!grid) {
    return null
  }

  const loadOptions = {
    ...grid.getDataSource().loadOptions()
  }

  loadOptions.filter = []

  let selectionFilter = grid.option('selectionFilter')

  if (selectionFilter && selectionFilter.length) {
    if (loadOptions.filter.length) {
      loadOptions.filter.push('and')
    }

    const dbTable = grid.option('dbTable')

    if (dbTable) {
      const key = grid.getDataSource().key()

      selectionFilter = JSON.stringify(selectionFilter)
      selectionFilter = selectionFilter.replaceAll(key, `${dbTable}.${key}`)
      selectionFilter = JSON.parse(selectionFilter)
    }

    loadOptions.filter.push(selectionFilter)
  }

  return loadOptions
}

export const buildNumberFormatByDecimals = (numOfDecimals = 0) => {
  let base = '#,##0' // ex. #,##0.0000 for 4 decimals

  if (numOfDecimals > 0) {
    base += '.' + '0'.repeat(numOfDecimals)
  }

  return base
}

export const handleRowValidatingScrollToError = (e, dataGridEditor) => {
  if (e.isValid) {
    return
  }

  setTimeout(() => {
    const gridInstance = dataGridEditor.component
    if (!gridInstance) return

    const firstInvalidCell = e.brokenRules[0]?.column?.dataField
    const rowIndex = e.component.getRowIndexByKey(e.key)

    if (rowIndex !== -1 && firstInvalidCell) {
      const rowElement = gridInstance.getRowElement(rowIndex)
      const cellElement = gridInstance.getCellElement(rowIndex, firstInvalidCell)

      if (rowElement && cellElement) {
        const scrollable = gridInstance.getScrollable()
        if (scrollable) {
          scrollable.scrollToElement(rowElement)

          setTimeout(() => {
            const gridRect = gridInstance.element().getBoundingClientRect()
            const cellRect = cellElement.getBoundingClientRect()
            const isLeft = cellRect.left < gridRect.left
            const isRight = cellRect.right > gridRect.right

            if (isLeft || isRight) {
              const scrollLeft = scrollable.scrollLeft() + (cellRect.left - gridRect.left) - 20
              scrollable.scrollTo({ left: scrollLeft })
            }

            setTimeout(() => cellElement.focus(), 200)
          }, 300)
        }
      }
    }
  }, 100)
}

export const handleTreeListRowValidatingScrollToError = (e, treeListEditor) => {
  if (e.isValid) {
    return
  }

  setTimeout(() => {
    const treeListInstance = treeListEditor.component
    if (!treeListInstance) return

    const firstInvalidCell = e.brokenRules[0]?.column?.dataField
    const rowIndex = treeListInstance.getRowIndexByKey(e.key)

    if (rowIndex !== -1 && firstInvalidCell) {
      const rowElement = treeListInstance.getRowElement(rowIndex)[0]
      const cellElement = treeListInstance.getCellElement(rowIndex, firstInvalidCell)

      if (rowElement && cellElement) {
        const scrollable = treeListInstance.getScrollable()
        if (scrollable) {
          scrollable.scrollToElement(rowElement)

          setTimeout(() => {
            const treeListRect = treeListInstance.element().getBoundingClientRect()
            const cellRect = cellElement.getBoundingClientRect()
            const isLeft = cellRect.left < treeListRect.left
            const isRight = cellRect.right > treeListRect.right

            if (isLeft || isRight) {
              const scrollLeft = scrollable.scrollLeft() + (cellRect.left - treeListRect.left) - 20
              scrollable.scrollTo({ left: scrollLeft })
            }

            setTimeout(() => cellElement.focus(), 200)
          }, 300)
        }
      }
    }
  }, 100)
}

export const buildTreeListParentSummary = (data, key, parentKey, setup) => {
  const summaryData = []

  data.filter((value, index, self) => {
    return value[parentKey] &&
      index === self.findIndex((t) => (
        t[parentKey] === value[parentKey]
      ))
  }).map(m => m[parentKey]).forEach(parentId => {
    const summaryEntry = {
      summaryRow: true,
      id: `${parentId}-summary`,
      [parentKey]: parentId
    }

    const parent = data.find(f => f[key] === parentId)
    const children = data.filter(f => f[parentKey] === parentId)

    setup.forEach(s => {
      let value = null

      if (s.function === 'count') {
        const count = formatNumber(children.length, NumberFormat.display)
        value = renderToString(<Trans i18nKey='ui.count' />) + ': ' + count
      } else if (s.function === 'sum') {
        const sum = formatNumber(
          children.reduce((accum, item) => {
            let value = item[s.field]

            if (s.multipleField) {
              value = value * item[s.multipleField]
            }

            if (s.divisionParentField) {
              value = value / parent[s.divisionParentField]
            }

            return accum + value
          }, 0),
          NumberFormat.display
        )
        value = renderToString(<Trans i18nKey='ui.sum' />) + ': ' + sum
      }

      summaryEntry[s.field] = value
    })

    summaryData.push(summaryEntry)
  })

  return summaryData
}

export const buildTreeListTotalSummary = (data, setup) => {
  const summaryData = []

  const summaryEntry = {
    summaryRow: true,
    id: 'total-summary'
  }

  setup.forEach(s => {
    let value = null

    if (s.function === 'count') {
      const count = formatNumber(data.length, NumberFormat.display)
      value = renderToString(<Trans i18nKey='ui.count' />) + ': ' + count
    } else if (s.function === 'sum') {
      const sum = formatNumber(
        data.reduce((accum, item) => accum + parseFloat(item[s.field]), 0),
        NumberFormat.display
      )
      value = renderToString(<Trans i18nKey='ui.sum' />) + ': ' + sum
    }

    summaryEntry[s.field] = value
  })

  summaryData.push(summaryEntry)

  return summaryData
}

export const buildLoadOptions = (dataGridRef, initialFilters = []) => {
  const grid = dataGridRef?.component
    ? dataGridRef.component
    : dataGridRef?.current?.instance

  const loadOptions = { ...grid.getDataSource().loadOptions() }

  loadOptions.filter = [...initialFilters]

  // const combinedFilter = grid.getCombinedFilter()
  //
  // if (combinedFilter) {
  //   loadOptions.filter.push(combinedFilter)
  // }

  let selectionFilter = grid.option('selectionFilter')

  if (selectionFilter && selectionFilter.length) {
    if (loadOptions.filter.length) {
      loadOptions.filter.push('and')
    }

    const dbTable = grid.option('dbTable')

    if (dbTable) {
      const key = grid.getDataSource().key()
      const pattern = new RegExp(`"${key}"`, 'g')
      selectionFilter = JSON.parse(
        JSON.stringify(selectionFilter).replace(pattern, `"${dbTable}.${key}"`)
      )

      // selectionFilter = JSON.stringify(selectionFilter)
      // selectionFilter = selectionFilter.replaceAll(key, `${dbTable}.${key}`)
      // selectionFilter = JSON.parse(selectionFilter)
    }

    loadOptions.filter.push(selectionFilter)
  }

  return loadOptions
}

export const buildTreeListLoadOptions = (
  treeListRef,
  initialFilters = []
) => {
  const tree = treeListRef?.current?.instance
  if (!tree) return {}

  const loadOptions = { ...tree.getDataSource().loadOptions() }
  loadOptions.filter = [...initialFilters]

  const combined = tree.getCombinedFilter()
  if (combined) {
    if (loadOptions.filter.length) loadOptions.filter.push('and')
    loadOptions.filter.push(combined)
  }

  let selectionFilter = tree.option('selectionFilter')

  if (!(selectionFilter && selectionFilter.length)) {
    const selectedKeys = tree.getSelectedRowKeys()
    if (selectedKeys.length) {
      const keyExpr =
        tree.option('keyExpr') || tree.getDataSource().key()
      selectionFilter = [[keyExpr, 'in', selectedKeys]]
    }
  }

  if (selectionFilter && selectionFilter.length) {
    if (loadOptions.filter.length) loadOptions.filter.push('and')

    const dbTable = tree.option('dbTable')
    if (dbTable) {
      const key = tree.getDataSource().key()
      const pattern = new RegExp(`"${key}"`, 'g')
      selectionFilter = JSON.parse(
        JSON.stringify(selectionFilter).replace(pattern, `"${dbTable}.${key}"`)
      )
    }

    loadOptions.filter.push(selectionFilter)
  }

  return loadOptions
}

export const dataGridIsSelectAllHandler = (e, isSelectAll) => {
  if (e.rowType === 'header' && e.columnIndex === 0) {
    const selectAll = CheckBox.getInstance(e.cellElement.querySelector('.dx-select-checkbox'))

    if (selectAll) {
      selectAll.on('valueChanged', (e) => {
        isSelectAll.current = (e.value === null || e.value === undefined) ? true : e.value
      })
    }
  }
}

export const scrollToLines = (dataGridRef) => {
  if (dataGridRef.current) {
    dataGridRef.current.instance.element().scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

export const setTagBoxEditorFilterRow = (e, dataGridRef, dataField, dataSource, displayExpr, valueExpr) => {
  if (e.parentType === 'filterRow' && e.dataField === dataField) {
    e.editorName = 'dxTagBox'
    e.editorOptions.dataSource = dataSource
    e.editorOptions.displayExpr = displayExpr
    e.editorOptions.valueExpr = valueExpr

    e.editorOptions.showSelectionControls = true
    e.editorOptions.closeOnOutsideClick = true
    e.editorOptions.showClearButton = true
    e.editorOptions.wrapItemText = true
    e.editorOptions.maxDisplayedTags = 2
    e.editorOptions.applyValueMode = 'useButtons'
    e.editorOptions.value = e.filterValue || []
    e.editorOptions.dropDownOptions = {
      width: 250
    }

    e.editorOptions.onValueChanged = function (args) {
      e.setValue(args.value)
      dataGridRef.current.instance.repaint()
      setTimeout(() => {
        dataGridRef.current.instance.updateDimensions()
      })
    }
  }
}

export function calculateFilterExpressionTagBoxFilterRow (filterValue, selectedFilterOperation, target) {
  const column = this

  if (!filterValue || filterValue.length === 0) return null

  if (target === 'search') {
    return calculateFilterExpressionField(filterValue, selectedFilterOperation, target, column)
  }

  if (Array.isArray(filterValue)) {
    const filters = filterValue.map(v =>
      calculateFilterExpressionField(v, '=', target, column)
    )

    return filters.reduce((acc, f) => {
      if (!acc) return f
      return [acc, 'or', f]
    }, null)
  }

  return calculateFilterExpressionField(filterValue, '=', target, column)
  // return calculateFilterExpressionField(filterValue, 'in', target, column)
}
